/**
 * QA session (plan §A5). Decided by the bootstrap script in index.html BEFORE
 * any analytics initialise: a `?qa=<signed token>` URL is verified by the
 * verifyQaToken callable and, if valid, stored in sessionStorage (dies with
 * the tab). A bare flag never enters QA mode.
 *
 * In a QA session: GA4 → the QA property (VITE_QA_GA4_ID, or disabled),
 * Google Ads tag not loaded, Meta pixel normal (use Test Events), Loops
 * skipped, and the signed token rides in Paddle custom_data.qa_token so the
 * server can verify it again (test_event_code + `qa: true` outbox docs).
 */

export const QA_SESSION_KEY = 'kidneypal_qa_session'

interface KpAnalyticsState {
  qa: boolean
  /** GA4 measurement id events go to, or null when GA4 is disabled. */
  ga4Id: string | null
  /** false in QA sessions: the Google Ads tag is not loaded. */
  adsEnabled: boolean
}

declare global {
  interface Window {
    kpAnalytics?: KpAnalyticsState
    kpAnalyticsReady?: Promise<void>
  }
}

const PROD_DEFAULTS: KpAnalyticsState = { qa: false, ga4Id: 'G-TVR0HLECHD', adsEnabled: true }

export function analyticsState(): KpAnalyticsState {
  return (typeof window !== 'undefined' && window.kpAnalytics) || PROD_DEFAULTS
}

export function isQaSession(): boolean {
  return analyticsState().qa === true
}

/** The verified, unexpired signed token for Paddle custom_data — never a boolean. */
export function getQaToken(): string | null {
  if (!isQaSession()) return null
  try {
    const raw = sessionStorage.getItem(QA_SESSION_KEY)
    if (!raw) return null
    const s = JSON.parse(raw) as { token?: unknown; expiresAtMs?: unknown }
    if (typeof s.token !== 'string' || typeof s.expiresAtMs !== 'number' || s.expiresAtMs <= Date.now()) return null
    return s.token
  } catch {
    return null
  }
}
